import { History, MoreHorizontal, PackagePlus, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { InventoryLevel, StockLocation } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { isLowStock } from '../schemas/inventory-rules';

import type { LevelAction } from './inventory-columns';
import { LowStockBadge } from './stock-badges';

export interface ItemLevelsCardProps {
  storeName: string;
  levels: readonly InventoryLevel[];
  locations: readonly StockLocation[];
  canWrite: boolean;
  onAction: (action: LevelAction, level: InventoryLevel) => void;
}

/** One row per location of the store, default first, like Shopify's "Inventory" card. */
export function ItemLevelsCard({
  storeName,
  levels,
  locations,
  canWrite,
  onAction,
}: ItemLevelsCardProps) {
  const { t } = useTranslation('inventory');
  const format = useFormat();
  const locationOf = (id: string) => locations.find((location) => location.id === id);

  return (
    <Card className="gap-0 py-0" data-tour="inventory.item.locations">
      <CardHeader className="px-6 py-4">
        <CardTitle className="text-sm">{t('item.locationsTitle')}</CardTitle>
        <CardDescription>{t('item.locationsDescription', { store: storeName })}</CardDescription>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">{t('columns.location')}</TableHead>
              <TableHead className="text-right">{t('columns.onHand')}</TableHead>
              <TableHead className="text-right">{t('columns.reserved')}</TableHead>
              <TableHead className="text-right">{t('columns.available')}</TableHead>
              <TableHead className="text-right">{t('columns.reorderPoint')}</TableHead>
              <TableHead>{t('columns.updated')}</TableHead>
              <TableHead className="w-12 pr-6">
                <span className="sr-only">{t('columns.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {levels.map((level) => {
              const location = locationOf(level.locationId);
              const name = location?.name ?? '—';
              return (
                <TableRow
                  key={level.locationId}
                  className={canWrite ? 'cursor-pointer' : undefined}
                  onClick={canWrite ? () => onAction('adjust', level) : undefined}
                >
                  <TableCell className="pl-6">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{name}</span>
                      {location?.isDefault ? (
                        <Badge variant="secondary">{t('locations.default')}</Badge>
                      ) : null}
                      {location && !location.isActive ? (
                        <Badge variant="outline">{t('locations.inactive')}</Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{level.onHand}</TableCell>
                  <TableCell className="text-right tabular-nums">{level.reserved}</TableCell>
                  <TableCell className="text-right">
                    <span className="flex items-center justify-end gap-2">
                      {isLowStock(level) ? <LowStockBadge /> : null}
                      <span className="font-medium tabular-nums">{level.available}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {level.reorderPoint ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {level.updatedAt ? (
                      <span title={format.utc(level.updatedAt)}>
                        {format.relative(level.updatedAt)}
                      </span>
                    ) : (
                      t('item.notStocked')
                    )}
                  </TableCell>
                  <TableCell className="pr-6">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('actions.menu', { sku: name })}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {canWrite ? (
                          <>
                            <DropdownMenuItem onSelect={() => onAction('adjust', level)}>
                              <PackagePlus aria-hidden />
                              {t('actions.adjust')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onAction('reorderPoint', level)}>
                              <Target aria-hidden />
                              {t('actions.reorderPoint')}
                            </DropdownMenuItem>
                          </>
                        ) : null}
                        <DropdownMenuItem onSelect={() => onAction('movements', level)}>
                          <History aria-hidden />
                          {t('actions.movements')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
